import type { ReactNode } from "react";
import dayjs, { Dayjs } from "dayjs";
import type {
  CalendarCellData,
  CalendarEventRef,
} from "./useCalendarioLogic";

export const ISO_FMT = "YYYY-MM-DD";

export const moneyFmt = new Intl.NumberFormat("es-CL");

// ===== Arriendos como barras continuas en vista mensual =====
/**
 * Un "span" en el calendario mensual: segmento continuo que se renderiza
 * como una sola barra (en vez de un chip por día). Usado tanto para
 * arriendos como para bloqueos/mantenciones del cliente.
 *
 * CRÍTICO — posicionamiento por tercios (ver docs/calendario-logica.md §2):
 *   Un día se divide en 3 tercios equidistantes gráficamente, con cortes
 *   en 11:00 (1/3) y 15:00 (2/3). Una barra que arranca 15:00 (evening)
 *   debe iniciarse en el 66.67% del ancho del día correspondiente; una
 *   barra que termina 11:00 (morning) debe terminar en el 33.33% del día
 *   correspondiente. Así un bloqueo "15:00 día A → 11:00 día B" se ve
 *   cruzando la noche entre ambos días — idéntico a la vista admin.
 *
 *   Sin esto, la barra del cliente cubriría solo el día A por completo y
 *   el día B parecería vacío (que es cómo estaba antes de este fix).
 */
export type DaySpan = {
  key: string;
  kind: "booking" | "block" | "maintenance" | "cleaning";
  /** Primer día que la barra toca visualmente (inclusive). */
  barStartISO: string;
  /** Último día que la barra toca visualmente (inclusive). */
  barEndISO: string;
  /**
   * Fracción (0..1) del ancho del día donde la barra INICIA, dentro del
   * barStartISO. Valores canónicos: 0 (morning), 1/3 (mid), 2/3 (evening).
   */
  startFrac: number;
  /**
   * Fracción (0..1) del ancho del día donde la barra TERMINA, dentro del
   * barEndISO. Valores canónicos: 1/3 (morning cutoff), 2/3 (mid cutoff),
   * 1 (evening completo).
   */
  endFrac: number;
  data?: any;
  isOwn?: boolean;
  label?: ReactNode;
  tooltip?: ReactNode;
  isFromClient?: boolean;
  sourceId?: number | string;
};

// ---- Mapping hora -> fracción de tercio ---------------------------------
// Debe ser idéntico a la lógica de `calendarioGrid.tsx` (admin). Ver
// docs/calendario-logica.md §2.1.
export const THIRD_FRAC = {
  morningEnd: 1 / 3,
  midStart: 1 / 3,
  midEnd: 2 / 3,
  eveningStart: 2 / 3,
};

/** Fracción X de inicio según minutos del día (cuantiza a tercios). */
export function startFracFromMinutes(min: number): number {
  if (min < 11 * 60) return 0;
  if (min < 15 * 60) return THIRD_FRAC.midStart;
  return THIRD_FRAC.eveningStart;
}
/** Fracción X de término según minutos del día (cuantiza a tercios). */
export function endFracFromMinutes(min: number): number {
  if (min <= 0) return 0;
  if (min <= 11 * 60) return THIRD_FRAC.morningEnd;
  if (min <= 15 * 60) return THIRD_FRAC.midEnd;
  return 1;
}

/** Parsea "HH:mm" / "HH:mm:ss" → minutos del día (0..1439). Null-safe. */
export function minutesFromHHmm(hhmm?: string | null, fallback = 0): number {
  if (!hhmm) return fallback;
  const parts = hhmm.split(":");
  const h = Number(parts[0]);
  const m = Number(parts[1] ?? 0);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return fallback;
  return h * 60 + m;
}

// Alias legacy — antes sólo había arriendos; ahora DaySpan cubre todo.
export type BookingSpan = DaySpan;

export function isoDate(d: Dayjs) {
  return d.format(ISO_FMT);
}

export function clampDay(d: Dayjs, min: Dayjs, max: Dayjs) {
  if (d.isBefore(min, "day")) return min;
  if (d.isAfter(max, "day")) return max;
  return d;
}

export function makeBookingKey(b: any): string {
  return String(b?.id ?? b?.bookingDbId ?? b?.bookingId ?? JSON.stringify(b));
}

export function bookingLabel(b: any): string {
  const estado = (b?.estado ?? "").toString().toLowerCase();
  const total = Number(b?.reservaTotalPropietario);

  if (estado === "reserved") return "Por confirmar";

  if (estado === "booked") {
    const totalTxt = Number.isFinite(total)
      ? ` $${moneyFmt.format(total)}`
      : "";
    return `Reservado${totalTxt}`;
  }

  return "Arriendo";
}

/**
 * Asigna carriles (lanes) a cada span dentro de una semana.
 *
 * Antes: la superposición se medía a nivel de DÍA. Dos bloqueos en días
 * contiguos (ej: `D1 15:00 → D2 10:59` y `D2 15:00 → D3 10:59`) compartían
 * el día D2 visualmente, así que el algoritmo los ponía en lanes distintas,
 * aunque sus tercios NO se topaban (uno ocupa el evening de D2, el otro la
 * mañana de D2). Resultado: barras "corriéndose" en altura sin motivo.
 *
 * Ahora: la superposición se mide con posiciones FRACCIONARIAS del día
 * (`dayIdx + startFrac` / `dayIdx + endFrac`). Dos spans comparten lane si
 * y sólo si sus intervalos fraccionarios se intersectan. Así:
 *   - Bloqueo cliente D1 15:00→D2 10:59 → [0.667, 1.333]
 *   - Bloqueo cliente D2 15:00→D3 10:59 → [1.667, 2.333]
 * No se topan → mismo lane (misma altura en la grilla). Pero si ambos
 * cubren realmente el evening de D1, caen en lanes distintas como antes.
 *
 * Ver docs/calendario-logica.md §2.2.
 */
export function buildWeekLanes(weekStart: Dayjs, spans: BookingSpan[]) {
  const wStart = weekStart.startOf("day");
  const wEnd = weekStart.add(6, "day").startOf("day");

  const laneByKey = new Map<string, number>();
  const segByKey = new Map<
    string,
    { segStart: Dayjs; segEnd: Dayjs; startIdx: number; endIdx: number }
  >();

  const overlaps = spans
    .map((s) => {
      const start = dayjs(s.barStartISO);
      const end = dayjs(s.barEndISO);
      if (end.isBefore(wStart, "day")) return null;
      if (start.isAfter(wEnd, "day")) return null;
      const segStart = clampDay(start, wStart, wEnd);
      const segEnd = clampDay(end, wStart, wEnd);
      const startIdx = segStart.diff(wStart, "day");
      const endIdx = segEnd.diff(wStart, "day");
      // Posiciones fraccionarias dentro de la semana. Sólo aplicamos
      // startFrac si el span arranca DENTRO de la semana (no si fue
      // clampeado por la izquierda); análogo para endFrac. Esto evita
      // que un span que viene de semanas anteriores pierda "altura"
      // inicial cuando lo clampeamos a lunes.
      const startClamped = !start.isSame(segStart, "day");
      const endClamped = !end.isSame(segEnd, "day");
      const startPos = startIdx + (startClamped ? 0 : s.startFrac ?? 0);
      const endPos = endIdx + (endClamped ? 1 : s.endFrac ?? 1);
      return { key: s.key, segStart, segEnd, startIdx, endIdx, startPos, endPos };
    })
    .filter(Boolean) as {
    key: string;
    segStart: Dayjs;
    segEnd: Dayjs;
    startIdx: number;
    endIdx: number;
    startPos: number;
    endPos: number;
  }[];

  overlaps.sort((a, b) => a.startPos - b.startPos || a.endPos - b.endPos);

  // Cada entrada es el `endPos` del último span asignado a esa lane.
  // Epsilon para considerar "toca pero no se superpone" como libre:
  // ej. bloqueo A termina en 1.333 y bloqueo B arranca en 1.667.
  const laneEndPos: number[] = [];
  for (const seg of overlaps) {
    let lane = 0;
    for (; lane < laneEndPos.length; lane++) {
      if (laneEndPos[lane] <= seg.startPos) break;
    }
    if (lane === laneEndPos.length) laneEndPos.push(seg.endPos);
    else laneEndPos[lane] = seg.endPos;

    laneByKey.set(seg.key, lane);
    segByKey.set(seg.key, seg);
  }

  return { laneByKey, segByKey, laneCount: laneEndPos.length };
}

export function dateFromISO(iso?: string | null) {
  return iso ? iso.slice(0, 10) : null;
}

export const uniqEventsFromCell = (cell: CalendarCellData) => {
  const all = [
    ...(cell.thirds.morning.events ?? []),
    ...(cell.thirds.mid.events ?? []),
    ...(cell.thirds.evening.events ?? []),
  ];

  const map = new Map<string, CalendarEventRef>();

  for (const e of all) {
    // Usa el id real que tengas en tu CalendarEventRef
    // (ajusta "e.id" si tu ref usa bookingId / eventId / etc.)
    const id = (e as any).id ?? (e as any).eventId ?? (e as any).bookingId;
    const key = `${e.kind}:${id ?? JSON.stringify(e)}`;
    if (!map.has(key)) map.set(key, e);
  }

  return Array.from(map.values());
};

export function getEventKey(e: CalendarEventRef): string {
  if (e.kind === "booking") {
    const db = e.bookingDbId != null ? `db:${e.bookingDbId}` : null;
    const plat = e.bookingId ? `plat:${e.bookingId}` : null;

    // fallback si no hay ids (último recurso)
    const fallback = `fb:${e.plataforma ?? ""}|${e.codigoPlataforma ?? ""}|${e.ingreso ?? ""}|${e.salida ?? ""}`;

    return `booking:${db ?? plat ?? fallback}`;
  }

  if (e.kind === "cleaning") {
    return `cleaning:${e.cleaningId}`;
  }

  // blocked | maintenance
  return `${e.kind}:${e.id}`;
}

export function dedupeEventRefs(list: CalendarEventRef[]): CalendarEventRef[] {
  const map = new Map<string, CalendarEventRef>();

  for (const e of list) {
    const key = getEventKey(e);
    if (!map.has(key)) map.set(key, e);
  }

  return Array.from(map.values());
}

/**
 * Devuelve `true` cuando una celda del calendario no contiene NADA visible
 * (ningún arriendo, bloqueo, mantención ni aseo en ningún tercio del día).
 *
 * Edge case: aceptamos `merged` undefined/null porque `mergedByDate[iso]`
 * puede no existir si la celda está fuera del rango cargado — eso también
 * cuenta como "vacío" para efectos de UX (permite click → crear bloqueo).
 *
 * Usado por:
 *  - decisión click en celda → abrir detalle (si NO está vacía) vs abrir
 *    creador de bloqueo (si SÍ está vacía y es hoy/futuro);
 *  - render del placeholder de precio/tachado en celdas sin eventos.
 */
export function isMergedEmpty(merged?: CalendarCellData | null): boolean {
  if (!merged) return true;
  const hasAny =
    merged.hasAny ||
    merged.thirds?.morning?.state !== "empty" ||
    merged.thirds?.mid?.state !== "empty" ||
    merged.thirds?.evening?.state !== "empty";
  return !hasAny;
}
